<docs>
  This is only show for markers point (plus teleport) hover event
</docs>

<template>
  <div
    v-if="isScreenReady"
    v-show="shouldShowMarkerInfo && !isMarkerInfoActive"
    class="viewer-markers-hover"
    :style="markerInfoPosition"
  >
    <div
      class="viewer-markers-hover-inner"
      @mouseover="htmlMarkerMousein"
      @mouseleave="htmlMarkerMouseout"
    >
      <div v-show="currentMarker.type === 'point' || currentMarker.type === 'teleport'">
        <div class="viewer-markers-hover-thumbnail">
          <icon
            class="viewer-markers-hover-thumbnail-inner"
            :image="nextPanorama.thumbnail"
            :has-lazyload="true"
          />
        </div>
        <p
          v-html-safe="nextCategory"
          class="viewer-markers-hover-text"
        />
      </div>
      <div v-show="['tag', 'popup', 'arMakerTag', 'videoplay', 'pdf'].includes(currentMarker.type)">
        <p
          v-if="currentMarker.nameMulti && currentMarker.nameMulti[lang]"
          v-html-safe="currentMarker.nameMulti[lang]"
          class="viewer-markers-hover-text"
        />
        <p
          v-else
          v-html-safe="currentMarker.name"
          class="viewer-markers-hover-text"
        />
      </div>
      <div
        v-show="currentMarker.type === 'videoplay' && !isVideoPreviewing"
        class="video-setting"
      >
        <i-favorite
          v-if="favoritesMarkerTypes.includes(currentMarker.type)"
          category="marker"
          :item="currentMarker"
        />
        <button
          class="player-button"
          @click="toggleVideoPause"
        >
          <i
            class="isif"
            :class="{'isif-play': isVideoplayPaused, 'isif-pause': !isVideoplayPaused}"
          />
        </button>
        <button
          class="player-button"
          @click="toggleVideoMuted(!isVideoplayMuted)"
        >
          <i
            class="isif"
            :class="{'isif-volume-mute': isVideoplayMuted, 'isif-volume-on': !isVideoplayMuted}"
          />
        </button>
        <i-slider
          :min="0"
          :max="100"
          direction="vertical"
          :value="videoplayVolume"
          @input="setVideoplayVolume"
        />
      </div>
      <div class="decoration" />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { isMobile, isEmpty } from '~js/utils'
import { PromiseService } from '../../../api/utils'
import Icon from '../../Icon/index'
import IFavorite from '@/components/IFavorite'
import ISlider from '@/components/ISlider'

export default {
  name: 'ViewerMarkersHover',
  components: {
    Icon,
    IFavorite,
    ISlider
  },

  props: {
    markerPositionX: {
      type: Number,
      default () {
        return 0
      }
    },
    markerPositionY: {
      type: Number,
      default () {
        return 0
      }
    },
    isMouseOnMarkerInfo: {
      type: Boolean,
      default () {
        return false
      }
    },
    isMouseOnKrpanoMarker: {
      type: Boolean,
      default () {
        return false
      }
    },
    mouseenterMarker: {
      type: Function,
      default () {
        return () => {}
      }
    },
    mouseleaveMarker: {
      type: Function,
      default () {
        return () => {}
      }
    }
  },
  data () {
    return {
      videoplayVolume: 0,
      isVideoplayMuted: false,
      isVideoplayPaused: false,
      isVideoPreviewing: true,
      lang: this.$i18n.locale,
      timeout: null
    }
  },

  computed: {
    ...mapState('favorites', {
      favoritesMarkerTypes: state => state.acceptedMarkerTypes
    }),

    ...mapGetters([
      'currentMarker',
      'panoramas',
      'isMarkerInfoActive',
      'isScreenReady',
      'krpanoEl',
      'markers',
      'property'
    ]),

    shouldShowMarkerInfo () {
      if (!isMobile() && this.currentMarker.type === 'point' && this.currentMarker.nextPanoramaId) {
        return true
      } else if (!isMobile() && this.currentMarker.type === 'teleport' && this.currentMarker.nextBuildingId && this.currentMarker.nextPanoramaId) {
        return true
      } else if (!isMobile() && ['tag', 'popup', 'arMakerTag', 'pdf'].includes(this.currentMarker.type) && (this.currentMarker.nameMulti[this.lang] || this.currentMarker.name)) {
        return true
      } else if (!isMobile() && this.currentMarker.type === 'videoplay') {
        return true
      } else {
        return false
      }
    },

    nextPanorama () {
      if (isEmpty(this.panoramas) || this.panoramas.length === 0) {
        return {}
      }
      const nextPanorama = this.panoramas.find(panorama => panorama.objectId === this.currentMarker.nextPanoramaId) || this.currentMarker.teleportPanorama
      return nextPanorama || {}
    },

    markerInfoPosition () {
      if (!isMobile()) {
        return {
          left: `${this.markerPositionX}px`,
          top: `${this.markerPositionY}px`
        }
      }
      return {}
    },

    nextCategory () {
      if (this.nextPanorama.categoryMulti) {
        const categoryMulti = JSON.parse(this.nextPanorama.categoryMulti)
        return categoryMulti[this.lang] || this.nextPanorama.customCategory || this.$t(this.nextPanorama.category) || ''
      } else {
        return this.nextPanorama.customCategory || this.$t(this.nextPanorama.category) || ''
      }
    }
  },
  watch: {
    'currentMarker.objectId' () {
      if (this.currentMarker.type === 'videoplay') {
        this.syncVideoPreviewState()
      }
    },
    'currentMarker.type' (val) {
      if (val === 'videoplay') {
        this.startSyncingVideoState()
      } else {
        this.stopSyncingVideoState()
      }
    }
  },
  beforeDestroy () {
    this.stopSyncingVideoState()
  },
  methods: {
    ...mapActions([
      'setMarker',
      'pauseAudio',
      'pauseMusic',
      'pauseAllVideo',
      'realPausePilotPlaying'
    ]),

    htmlMarkerMousein () {
      this.$emit('setMouseOnMarkerInfo', true)
    },

    htmlMarkerMouseout () {
      this.$emit('setMouseOnMarkerInfo', false)
      this.$nextTick(() => {
        if (this.isMouseOnMarkerInfo === false &&
          this.isMouseOnKrpanoMarker === false) {
          this.mouseleaveMarker()
        }
      })
    },
    toggleVideoPause () {
      this.isVideoplayPaused = !this.isVideoplayPaused
      if (!this.isVideoplayPaused) {
        this.pauseAudio()
        this.pauseMusic()
        this.pauseAllVideo()
        this.realPausePilotPlaying()
      }
      window.krpanoObj.call(`hotspot[marker_${this.currentMarker.objectId}].togglepause()`)
    },
    toggleVideoMuted (val) {
      window.krpanoObj.call(`set_value(hotspot[marker_${this.currentMarker.objectId}].muted, ${val})`)
      this.isVideoplayMuted = val
    },
    async setVideoplayVolume (val) {
      const hotspotVolume = `hotspot[marker_${this.currentMarker.objectId}].volume`
      let volume = await new PromiseService('get_value').exec(() => window.krpanoObj.call(`get_value(${hotspotVolume})`))
      volume = val / 100
      window.krpanoObj.call(`set_value(${hotspotVolume}, ${volume})`)
    },
    async startSyncingVideoState () {
      const hotspotVolume = `hotspot[marker_${this.currentMarker.objectId}].volume`
      const hotspotMuted = `hotspot[marker_${this.currentMarker.objectId}].muted`
      const hotspotIsPaused = `hotspot[marker_${this.currentMarker.objectId}].ispaused`
      const isMuted = await new PromiseService('get_value').exec(() => window.krpanoObj.call(`get_value(${hotspotMuted})`))
      const volume = await new PromiseService('get_value').exec(() => window.krpanoObj.call(`get_value(${hotspotVolume})`))
      const isPaused = await new PromiseService('get_value').exec(() => window.krpanoObj.call(`get_value(${hotspotIsPaused})`))
      this.videoplayVolume = volume * 100
      this.isVideoplayMuted = isMuted
      this.isVideoplayPaused = isPaused
      this.timeout = setTimeout(this.startSyncingVideoState, 200)
    },
    stopSyncingVideoState () {
      clearTimeout(this.timeout)
    },
    async syncVideoPreviewState () {
      const videoPreviewScale = await new PromiseService('get_value').exec(() => window.krpanoObj.call(`get_value(hotspot[markerInfo_preview_${this.currentMarker.objectId}].scale)`))
      this.isVideoPreviewing = videoPreviewScale !== 0
    }
  }
}
</script>

<style lang="stylus" rel="stylesheet/stylus" scoped>
@import '~css/addon/main'
@import '~css/common/extends'
// @import '~css/add/color'

$width = 300px
$height = 145.8px
.viewer-markers-hover {
  position: absolute
  height: auto
  color: $white
  max-width: 660px
  max-height: 80%
  font-size: 20px
  z-index: $viewer-markers-hover-z
  transform: translate(-50%, -100%)
}

.viewer-markers-hover-inner {
  @extend .clear
  position: relative
  display: flex
  flex-direction: column
  background-color: alpha($dark-gray, 80%)
  padding: 10px
  align-self: center
  max-height: 80%
  z-index: $viewer-markers-hover-z
  border-radius: 4px
}

.viewer-markers-hover-thumbnail {
  position: relative
  width: $width
  height: $height
  margin-bottom: 5px
  overflow: hidden

  .viewer-markers-hover-thumbnail-inner {
    background-repeat: repeat
  }
}

.viewer-markers-hover-thumbnail-inner {
  width: inherit
  height: inherit
  background-size: $width $height
  animation-duration: .6s
  animation-name: point-thumbnail-animation
  animation-timing-function: ease-out
  animation-fill-mode: forwards

  @keyframes point-thumbnail-animation {
    from {
      transform: scale(1)
    }
    to {
      transform: scale(1.3)
    }
  }
}

.viewer-markers-hover-text {
  @extend .text-with-gray-bg
  position: relative
  line-height: 20px
  font-size: 15px
  text-align: center
  max-width: $width
  overflow: hidden
  word-break: break-word
}

.decoration {
  $bd = 10px
  position: absolute
  bottom: 0
  margin-bottom: -($bd * 2 - 1px)
  left: 50%
  transform: translateX(-50%)
  opacity: .8
  font-size: 0
  line-height: 0
  width: 0
  border-top: $bd solid $dark-gray
  border-bottom: $bd solid transparent
  border-left: $bd solid transparent
  border-right: $bd solid transparent
}
.player-button {
  padding:5px 10px
  border-radius: 3px
  &:active {
    color: $pink-color
  }
}

.video-setting {
  display: flex
  justify-content: space-evenly
  align-items: center
  margin-top: 5px

  .i-favorite {
    margin-right: 5px
    position: relative
    top: -1px
  }
  button {
    margin: 0px 2px
  }
  .slider {
    height: 0px
  }
}
</style>
